import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { RubricGrid, RubricLevel, RubricCriterion, RubricDescriptor } from '../types';

interface RubricBuilderProps {
  rubric: RubricGrid;
  onChange: (rubric: RubricGrid) => void;
  readOnly?: boolean;
}

export const RubricBuilder: React.FC<RubricBuilderProps> = ({ rubric, onChange, readOnly = false }) => {
  const generateId = () => Math.random().toString(36).substring(2, 9);

  const addLevel = () => {
    const newLevel: RubricLevel = {
      id: generateId(),
      name: `Level ${rubric.levels.length + 1}`,
      percentage: 50,
    };
    onChange({ ...rubric, levels: [...rubric.levels, newLevel] });
  };

  const updateLevel = (id: string, updates: Partial<RubricLevel>) => {
    onChange({
      ...rubric,
      levels: rubric.levels.map(l => l.id === id ? { ...l, ...updates } : l),
    });
  };

  const removeLevel = (id: string) => {
    onChange({
      ...rubric,
      levels: rubric.levels.filter(l => l.id !== id),
      descriptors: rubric.descriptors.filter(d => d.levelId !== id),
    });
  };

  const addCriterion = () => {
    const newCriterion: RubricCriterion = {
      id: generateId(),
      title: `Criterion ${rubric.criteria.length + 1}`,
      description: '',
      weight: 25,
    };
    onChange({ ...rubric, criteria: [...rubric.criteria, newCriterion] });
  };

  const updateCriterion = (id: string, updates: Partial<RubricCriterion>) => {
    onChange({
      ...rubric,
      criteria: rubric.criteria.map(c => c.id === id ? { ...c, ...updates } : c),
    });
  };

  const removeCriterion = (id: string) => {
    onChange({
      ...rubric,
      criteria: rubric.criteria.filter(c => c.id !== id),
      descriptors: rubric.descriptors.filter(d => d.criterionId !== id),
    });
  };

  const updateDescriptor = (criterionId: string, levelId: string, description: string) => {
    const existingIndex = rubric.descriptors.findIndex(
      d => d.criterionId === criterionId && d.levelId === levelId
    );
    
    let newDescriptors: RubricDescriptor[];
    if (existingIndex >= 0) {
      newDescriptors = rubric.descriptors.map((d, i) =>
        i === existingIndex ? { ...d, description } : d
      );
    } else {
      newDescriptors = [...rubric.descriptors, { criterionId, levelId, description }];
    }
    
    onChange({ ...rubric, descriptors: newDescriptors });
  };

  const getDescriptor = (criterionId: string, levelId: string): string => {
    return rubric.descriptors.find(
      d => d.criterionId === criterionId && d.levelId === levelId
    )?.description || '';
  };

  const totalWeight = rubric.criteria.reduce((sum, c) => sum + c.weight, 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-slate-800">Evaluation Rubric</h3>
        <div className={`text-sm font-medium ${totalWeight === 100 ? 'text-green-600' : 'text-red-600'}`}>
          Total Weight: {totalWeight}% {totalWeight !== 100 && '(must be 100%)'}
        </div>
      </div>

      {/* Performance Levels */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-slate-700">Performance Levels</h4>
          {!readOnly && (
            <button
              type="button"
              onClick={addLevel}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add Level
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {rubric.levels.map(level => (
            <div key={level.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              {readOnly ? (
                <>
                  <p className="font-medium text-slate-800">{level.name}</p>
                  <p className="text-sm text-slate-500">{level.percentage}%</p>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    value={level.name}
                    onChange={(e) => updateLevel(level.id, { name: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-slate-200 rounded mb-2"
                    placeholder="Level name"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={level.percentage}
                      onChange={(e) => updateLevel(level.id, { percentage: parseInt(e.target.value) || 0 })}
                      className="w-16 px-2 py-1 text-sm border border-slate-200 rounded"
                      min="0"
                      max="100"
                    />
                    <span className="text-sm text-slate-500">%</span>
                    <button
                      type="button"
                      onClick={() => removeLevel(level.id)}
                      className="ml-auto p-1 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Criteria */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-slate-700">Evaluation Criteria</h4>
          {!readOnly && (
            <button
              type="button"
              onClick={addCriterion}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add Criterion
            </button>
          )}
        </div>
        
        <div className="space-y-4">
          {rubric.criteria.map(criterion => (
            <div key={criterion.id} className="p-4 bg-white border border-slate-200 rounded-lg">
              <div className="flex gap-4 mb-3">
                {readOnly ? (
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{criterion.title}</p>
                    <p className="text-sm text-slate-500">{criterion.description}</p>
                    <p className="text-sm text-blue-600">Weight: {criterion.weight}%</p>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={criterion.title}
                        onChange={(e) => updateCriterion(criterion.id, { title: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg mb-2"
                        placeholder="Criterion title"
                      />
                      <input
                        type="text"
                        value={criterion.description}
                        onChange={(e) => updateCriterion(criterion.id, { description: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        placeholder="Description"
                      />
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={criterion.weight}
                          onChange={(e) => updateCriterion(criterion.id, { weight: parseInt(e.target.value) || 0 })}
                          className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-center"
                          min="0"
                          max="100"
                        />
                        <span className="text-sm text-slate-500">%</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCriterion(criterion.id)}
                        className="p-1 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
              
              {/* Descriptors Grid */}
              {rubric.levels.length > 0 && (
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${rubric.levels.length}, 1fr)` }}>
                  {rubric.levels.map(level => (
                    <div key={level.id} className="p-2 bg-slate-50 rounded">
                      <p className="text-xs font-medium text-slate-600 mb-1">{level.name}</p>
                      {readOnly ? (
                        <p className="text-xs text-slate-600">{getDescriptor(criterion.id, level.id) || '-'}</p>
                      ) : (
                        <textarea
                          value={getDescriptor(criterion.id, level.id)}
                          onChange={(e) => updateDescriptor(criterion.id, level.id, e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-slate-200 rounded resize-none"
                          rows={2}
                          placeholder="Describe this level..."
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
